import CloseRounded from "@mui/icons-material/CloseRounded";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getGoogleMapsApiKey } from "@bw/shared/components";
import { distanceOptions, radiusLabel, type MeasurementSystem } from "../distanceUnits";
import type { NearbyCenter } from "../hooks/useNearbyCenter";
import { MaterialAreaPicker } from "./MaterialAreaPicker";
import { NearbyControl } from "./NearbyControl";

export function DiscoveryAreaFilter({
  center,
  radiusKm,
  measurementSystem,
  onCenterChange,
  onRadiusChange,
  onClear,
}: {
  center: NearbyCenter | null;
  radiusKm: number;
  measurementSystem: MeasurementSystem;
  onCenterChange: (center: NearbyCenter) => void;
  onRadiusChange: (radiusKm: number) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const addressSearchAvailable = Boolean(getGoogleMapsApiKey());
  const [draft, setDraft] = useState(center?.label ?? "");
  useEffect(() => setDraft(center?.label ?? ""), [center?.label]);

  return (
    <>
      {center ? (
        <Chip
          icon={<LocationOnOutlined />}
          variant="outlined"
          label={`${center.label || "Current location"} · ${radiusLabel(radiusKm, measurementSystem)}`}
          onClick={() => setOpen(true)}
          onDelete={onClear}
          sx={{ borderRadius: 99, maxWidth: { xs: "100%", sm: 380 } }}
        />
      ) : (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<LocationOnOutlined />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 99, borderColor: "divider" }}
        >
          Location
        </Button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        fullWidth
        maxWidth="sm"
        aria-labelledby="discovery-area-title"
        sx={{
          "& .MuiDialog-container": {
            alignItems: { xs: "flex-end", sm: "center" },
          },
          "& .MuiDialog-paper": {
            m: { xs: 0, sm: 3 },
            width: { xs: "100%", sm: "calc(100% - 48px)" },
            borderRadius: { xs: "20px 20px 0 0", sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" id="discovery-area-title">
            Search area
          </Typography>
          <IconButton aria-label="Close location" onClick={() => setOpen(false)}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {addressSearchAvailable ? (
              <MaterialAreaPicker
                label="City, ZIP code, or area"
                placeholder="Milpitas, CA 95035"
                value={draft}
                onValueChange={setDraft}
                onAreaSelect={(area) => {
                  setDraft(area.label);
                  onCenterChange({
                    latitude: area.lat,
                    longitude: area.lng,
                    label: area.label,
                    coarse: true,
                  });
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Use your current location here, or close this sheet and enter a
                city in the adjacent City filter.
              </Typography>
            )}
            <NearbyControl
              center={center}
              hasLocation={Boolean(center)}
              onCenterChange={onCenterChange}
              onClearLocation={() => {
                setDraft("");
                onClear();
              }}
              locationCopy={
                center
                  ? `Searching within ${radiusLabel(radiusKm, measurementSystem)} of ${center.label || "your location"}.`
                  : "Choose an area or use your current location."
              }
            />
            <TextField
              select
              size="small"
              label="Distance"
              value={center ? radiusKm : ""}
              disabled={!center}
              onChange={(event) => onRadiusChange(Number(event.target.value))}
            >
              <MenuItem value="" disabled>
                Choose location
              </MenuItem>
              {distanceOptions(radiusKm, measurementSystem).map((choice) => (
                <MenuItem key={choice.value} value={choice.value}>
                  Within {choice.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={onClear} disabled={!center}>
            Clear
          </Button>
          <Button variant="contained" onClick={() => setOpen(false)}>
            Show results
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
